import { useState, useEffect } from 'react';
import { supabase } from '../../supabase';

function AdsDashboard({ setPaginaAtiva, setClienteAdsAtivo }) {
  const [clientes, setClientes] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [filtroCanal, setFiltroCanal] = useState('todos');
  const [periodo, setPeriodo] = useState('mes');

  useEffect(() => {
    carregarClientes();
  }, []);

  const carregarClientes = async () => {
    const { data, error } = await supabase
      .from('clientes')
      .select(`
        *,
        ads_config_clientes(*),
        ads_metricas_cliente(
          favorita,
          ads_metricas_config(nome, tipo)
        ),
        ads_lancamentos(data_ref, canal, valores),
        ads_metas(valor, ads_metricas_config(nome))
      `)
      .eq('modulo_ads', true)
      .eq('ativo', true);

    if (!error) setClientes(data || []);
    setCarregando(false);
  };

  const getUltimoLancamento = (lancamentos) => {
    if (!lancamentos?.length) return {};
    const sorted = [...lancamentos].sort((a, b) => new Date(b.data_ref) - new Date(a.data_ref));
    const merged = {};
    sorted.forEach(l => {
      Object.entries(l.valores || {}).forEach(([k, v]) => {
        if (!merged[k]) merged[k] = 0;
        merged[k] += Number(v);
      });
    });
    return merged;
  };

  const getMetricasFavoritas = (cliente) => {
    return cliente.ads_metricas_cliente
      ?.filter(m => m.favorita)
      ?.slice(0, 3) || [];
  };

  const formatarValor = (val, tipo) => {
    if (!val && val !== 0) return '—';
    if (tipo === 'moeda') return `R$ ${Number(val).toLocaleString('pt-BR', { minimumFractionDigits: 0 })}`;
    if (tipo === 'percentual') return `${Number(val).toFixed(1)}%`;
    if (tipo === 'multiplicador') return `${Number(val).toFixed(1)}x`;
    return Number(val).toLocaleString('pt-BR');
  };

  const clientesFiltrados = clientes.filter(c => {
    if (filtroCanal === 'meta') return c.ads_config_clientes?.canal_meta;
    if (filtroCanal === 'google') return c.ads_config_clientes?.canal_google;
    return true;
  });

  const totalGasto = clientes.reduce((acc, c) => {
    const vals = getUltimoLancamento(c.ads_lancamentos);
    return acc + (Number(vals['Gasto']) || 0);
  }, 0);

  if (carregando) {
    return (
      <div className="flex items-center justify-center h-64">
        <p style={{ color: '#555' }}>Carregando...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">Ads</h1>
        <select
          value={periodo}
          onChange={(e) => setPeriodo(e.target.value)}
          className="text-sm px-3 py-2 rounded-lg"
          style={{ backgroundColor: '#111', border: '1px solid #1a1a1a', color: '#aaa' }}
        >
          <option value="semana">Esta semana</option>
          <option value="mes">Este mês</option>
          <option value="mes_passado">Mês passado</option>
        </select>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Gasto total', val: `R$ ${(totalGasto / 1000).toFixed(1)}k` },
          { label: 'Clientes ativos', val: clientes.length },
          { label: 'Acima da meta', val: '—', cor: '#22c55e' },
          { label: 'Abaixo da meta', val: '—', cor: '#ef4444' },
        ].map((s, i) => (
          <div key={i} className="rounded-xl p-4" style={{ backgroundColor: '#111', border: '1px solid #1a1a1a' }}>
            <p className="text-xs mb-2" style={{ color: '#555' }}>{s.label}</p>
            <p className="text-xl font-bold" style={{ color: s.cor || '#fff' }}>{s.val}</p>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div className="flex gap-2 mb-4">
        {['todos', 'meta', 'google'].map(f => (
          <button
            key={f}
            onClick={() => setFiltroCanal(f)}
            className="text-xs px-4 py-1.5 rounded-full transition-all"
            style={{
              backgroundColor: filtroCanal === f ? '#DCF127' : '#111',
              color: filtroCanal === f ? '#0a0a0a' : '#666',
              border: filtroCanal === f ? 'none' : '1px solid #1a1a1a',
            }}
          >
            {f === 'todos' ? 'Todos' : f === 'meta' ? 'Meta' : 'Google'}
          </button>
        ))}
        <p className="text-xs ml-2 self-center" style={{ color: '#555' }}>
          {clientesFiltrados.length} clientes
        </p>
      </div>

      {/* Cards de clientes */}
      {clientesFiltrados.length === 0 ? (
        <div className="rounded-xl p-8 text-center" style={{ backgroundColor: '#111', border: '1px solid #1a1a1a' }}>
          <p style={{ color: '#555' }}>Nenhum cliente com Ads ativo.</p>
          <p className="text-xs mt-1" style={{ color: '#333' }}>Ative o módulo Ads na página de Clientes.</p>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-4">
          {clientesFiltrados.map((cliente) => {
            const config = cliente.ads_config_clientes;
            const valores = getUltimoLancamento(cliente.ads_lancamentos);
            const favoritas = getMetricasFavoritas(cliente);
            const gasto = Number(valores['Gasto']) || 0;

            return (
              <div
                key={cliente.id}
                onClick={() => {
                  setClienteAdsAtivo(cliente);
                  setPaginaAtiva('ads_cliente');
                }}
                className="rounded-xl p-5 cursor-pointer transition-all"
                style={{ backgroundColor: '#111', border: '1px solid #1a1a1a' }}
                onMouseEnter={(e) => e.currentTarget.style.border = '1px solid #333'}
                onMouseLeave={(e) => e.currentTarget.style.border = '1px solid #1a1a1a'}
              >
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-semibold text-white">{cliente.nome}</span>
                  <div className="flex gap-1">
                    {config?.canal_meta && (
                      <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: '#E6F1FB', color: '#185FA5' }}>Meta</span>
                    )}
                    {config?.canal_google && (
                      <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: '#EAF3DE', color: '#3B6D11' }}>Google</span>
                    )}
                  </div>
                </div>

                {/* Gasto */}
                <p className="text-2xl font-bold text-white mb-0.5">
                  {gasto > 0 ? `R$ ${gasto.toLocaleString('pt-BR')}` : '—'}
                </p>
                <p className="text-xs mb-4" style={{ color: '#555' }}>gasto no período</p>

                {/* Métricas favoritas */}
                {favoritas.length > 0 ? (
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    {favoritas.map((m, i) => (
                      <div key={i} className="rounded-lg p-2" style={{ backgroundColor: '#1a1a1a' }}>
                        <p className="text-xs mb-1" style={{ color: '#555' }}>{m.ads_metricas_config?.nome}</p>
                        <p className="text-sm font-semibold text-white">
                          {formatarValor(valores[m.ads_metricas_config?.nome], m.ads_metricas_config?.tipo)}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="mb-4 p-2 rounded-lg text-center" style={{ backgroundColor: '#1a1a1a' }}>
                    <p className="text-xs" style={{ color: '#333' }}>Configure métricas favoritas</p>
                  </div>
                )}

                {/* Rodapé */}
                <div className="flex items-center justify-between pt-3" style={{ borderTop: '1px solid #1a1a1a' }}>
                <span className="text-xs px-2 py-1 rounded-full" style={{ background: '#1a2a1a', color: '#22c55e' }}>
                    sem dados de meta
                </span>
                <div className="flex gap-2">
                    <button
                    onClick={(e) => {
                        e.stopPropagation();
                        setClienteAdsAtivo(cliente);
                        setPaginaAtiva('ads_lancamento');
                    }}
                    className="text-xs px-3 py-1 rounded-lg transition-all"
                    style={{ backgroundColor: '#1a1a1a', color: '#aaa', border: '1px solid #222' }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = '#DCF127'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = '#aaa'; }}
                    >
                    + lançar
                    </button>
                    <span style={{ color: '#333' }}>→</span>
                </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default AdsDashboard;